/**
 * 地址輸入:郵遞區號小格 + 地址大格(REQ-20260901004)。
 *
 * **資料層完全不動** —— 對外仍然只認一整串地址字串。舊 mdb 匯入時就把
 * 「地址 + 郵遞區號」併成一欄(docs/REQ-20260519001-erp-extension/MDB-IMPORT-PLAN.md),
 * 現況資料長成「508-42 彰化縣和美鎮新中路68號」;同文件已把「區域 / 縣市 / 鄉鎮
 * reference 表(郵遞區號 / 地址自動補)」列為**獨立 REQ**,所以這裡不做對照表、
 * 不做縣市自動帶入、不新增 zipCode 欄位,只是把同一個字串在畫面上拆成兩格編輯。
 *
 * 兩格的內容一律從 `value` 重新切,只有「`value` 就是自己上一次併出來的那串」時才沿用
 * 當時的兩格原文(理由見 `AddressInput` 內的註解)—— 呼叫端把地址換掉 / 清空時,
 * 區號不會黏在格子裡。
 *
 * REQ-20260901005 起,區號格**只收數字、最多 7 碼**(過濾只作用在使用者這次的輸入,
 * 既有值照原樣顯示),切字規則從「3 碼」放寬到「3~7 碼」以吃下新式 6 碼區號。
 *
 * REQ-20260901006 再把寬度縮到 88px 的 4/5 = **70.4px**,並在元件內以 `!px-1 !text-sm`
 * 覆寫各表單的左右內距與字級 —— 不縮 padding 就放不下 7 碼(見下方註解的實測數字)。
 */
import { useRef, type ReactNode } from 'react';

/**
 * 只認開頭的台灣區號:3 碼 + `-` + 2~3 碼(`508-42`),或 3~7 碼純數字
 * (`500` / 新式 6 碼 `514005` / 7 碼)。區號後面要嘛是空白,要嘛直接接非數字的
 * 地址字元(舊資料兩種都有)。
 *
 * **帶橫線那一支一定要排在前面** —— 反過來的話 `\d{3,7}` 會先吃掉 `508`,
 * 後面緊接的 `-` 兩個條件(空白 / 非數字地址字元)都不符,`508-42 …` 整條反而匹配失敗。
 *
 * 原本只認 3 碼,新式 6 碼區號「514005 彰化縣溪湖鎮李厝路3號」整串會被丟進地址大格;
 * REQ-20260901005 放寬成 3~7 碼。**已知且刻意接受的行為改變**:
 * 「12345 台北市中正區」以前不拆,現在會拆成 `12345` + `台北市中正區`;
 * 8 碼以上開頭仍然不拆。
 */
const ZIP_PREFIX_RE = /^\s*(\d{3}-\d{2,3}|\d{3,7})(?:\s+|(?=[^\d\s-]))([\s\S]*)$/;

/**
 * 把「508-42 彰化縣和美鎮新中路68號」切成 `{ zip, rest }`。
 * 認不出區號時(含 null / 空字串 / 開頭不是 3~7 碼數字)整串留在 `rest`,不臆測。
 */
export function splitAddressZip(addr: string | null | undefined): { zip: string; rest: string } {
  const m = ZIP_PREFIX_RE.exec(addr ?? '');
  if (!m) return { zip: '', rest: addr ?? '' };
  return { zip: m[1] ?? '', rest: (m[2] ?? '').trim() };
}

/**
 * `splitAddressZip()` 的反向:兩格併回單一字串,中間一個半形空格。
 *
 * 地址那一段只去頭不去尾 —— 去尾的話,使用者在地址結尾按下的空白鍵會讓併出來的字串
 * 與上一次相同,React 的受控 input 會把那個空白還原掉,「新中路68號 3樓」這種中間有
 * 空白的地址就永遠打不出來。整格都是空白時仍視為空。
 */
export function joinAddressZip(zip: string, rest: string): string {
  const z = zip.trim();
  const r = rest.trim() === '' ? '' : rest.trimStart();
  if (!z) return r;
  if (!r) return z;
  return `${z} ${r}`;
}

export interface AddressInputProps {
  /** 欄位名稱,用來組兩格各自的 aria-label(外層不再用包住 input 的 `<label>`) */
  label: string;
  value: string | null | undefined;
  /** 收到的是**合併後**的完整地址;空字串要轉成 null 還是留 '' 由呼叫端自己決定 */
  onChange: (next: string) => void;
  /** 呼叫端既有的 input class,兩格共用 —— 各表單樣式不同,不在這裡自己寫一套 */
  inputClassName: string;
  disabled?: boolean | undefined;
  /** 地址大格的 placeholder */
  placeholder?: string | undefined;
  /** 郵遞區號小格的 placeholder(預設「郵遞區號」) */
  zipPlaceholder?: string | undefined;
  /** 尾端插槽(例:客戶 / 廠商表單的地圖按鈕,見 components/LinkOutButtons) */
  children?: ReactNode;
}

export function AddressInput(props: AddressInputProps): JSX.Element {
  /**
   * 兩格的內容原則上就是 `splitAddressZip(value)` 的結果,**只有**當 `value` 正是自己
   * 上一次併出來的那一串時才沿用當時的兩格原文。少了這一段,打到一半的區號會跳格:
   * 區號打「5」→ 併成 `"5"` → 再切時「5」不合區號規則 → 整個跑到地址大格。
   * 反過來,只要 `value` 是外面換的(切換客戶、reset、被清空),比對就對不上,
   * 兩格一律重切 —— 「地址清空了區號還黏著」這種不同步不會發生。
   */
  const draftRef = useRef<{ merged: string; zip: string; rest: string } | null>(null);
  const draft = draftRef.current;
  const { zip, rest } =
    draft !== null && draft.merged === (props.value ?? '') ? draft : splitAddressZip(props.value);

  const emit = (nextZip: string, nextRest: string): void => {
    const merged = joinAddressZip(nextZip, nextRest);
    draftRef.current = { merged, zip: nextZip, rest: nextRest };
    props.onChange(merged);
  };

  return (
    <div className="flex gap-1">
      {/*
        寬度用 `basis-*` 而不是 `w-*`:CustomerForm / SupplierForm 的 inputCls 自帶
        `w-full`,而 Tailwind 產出的 CSS 裡 `.w-full` 排在 `.w-24` 之類後面會勝出。
        `min-w-0` 也不能省 —— flex item 的自動最小寬度是 input 的預設 size(實測 172px),
        沒解開的話根本壓不下去(設定頁那兩格的 inputCls 沒有 `w-full`,最明顯)。

        寬度 96px(REQ-20260901004)→ 88px(REQ-20260901005)→ **70.4px**(`4.4rem`,
        REQ-20260901006 指定的「再縮 1/5」)。

        `!px-1 !text-sm` 是把 70.4px 擠出來的必要條件,不是順手統一樣式:7 碼
        「5140056」的 ink 寬就有 56px,沿用各表單原本的 padding / 字級(出貨單 px-3、
        設定頁繼承 16px)時最後一碼會被切掉。important 修飾也不能省 —— 同層級的
        `.px-1` 在 Tailwind 產出的 CSS 裡排在 `.px-2` / `.px-3` 前面,不加 `!` 打不贏。
        真 Chrome 實測(px-1 後三種 inputCls 的可用內容寬都是 60.39px):
        7 碼 56.01px、placeholder「郵遞區號」56px,三格都 `scrollWidth === clientWidth`。
        高度不受影響 —— 沒動 `py`,而且外層 flex 預設 `align-items: stretch`,
        區號格會被拉到與地址格同高(實測 30/30、38/38)。
      */}
      {/*
        區號只留數字、最多 7 碼(`maxLength` 擋不住貼上的非數字,所以還是要自己過濾)。
        過濾只作用在**使用者這次輸入的內容** —— 從 `value` 切出來的既有值(舊資料的
        `508-42`)在被編輯前照原樣顯示,不在 render 時過濾:否則一進畫面資料就被默默
        改掉,還會讓 RHF 的 `isDirty` 誤觸發未存離開攔截。
      */}
      <input
        type="text"
        value={zip}
        onChange={(e) => emit(e.target.value.replace(/\D/g, '').slice(0, 7), rest)}
        placeholder={props.zipPlaceholder ?? '郵遞區號'}
        inputMode="numeric"
        maxLength={7}
        className={`${props.inputClassName} basis-[4.4rem] shrink-0 min-w-0 !px-1 !text-sm`}
        disabled={props.disabled}
        aria-label={`${props.label}郵遞區號`}
      />
      <input
        type="text"
        value={rest}
        onChange={(e) => emit(zip, e.target.value)}
        placeholder={props.placeholder}
        className={`${props.inputClassName} grow min-w-0`}
        disabled={props.disabled}
        aria-label={props.label}
      />
      {props.children}
    </div>
  );
}
